import { useQuery } from "@tanstack/react-query";
import { getProjectWithRfi } from "@/lib/api/projects";
import type { ProjectMemberRole } from "@/types/backend";

const ROLE_LEVELS: Record<ProjectMemberRole, number> = {
  viewer: 1,
  editor: 2,
  manager: 3,
};

export function roleCanEdit(role: ProjectMemberRole | null | undefined): boolean {
  return role ? ROLE_LEVELS[role] >= ROLE_LEVELS.editor : false;
}

export function roleCanManageMembers(role: ProjectMemberRole | null | undefined): boolean {
  return role === "manager";
}

export const NO_PERMISSION_TOOLTIP = "Only editors and managers can do this.";

export interface UseProjectRoleResult {
  role: ProjectMemberRole | null;
  isLoading: boolean;
  canView: boolean;
  canEdit: boolean;
  canManageMembers: boolean;
}

/**
 * Reads the current user's role for a project. Reuses the shared
 * ['project', projectId] query so callers don't trigger an extra fetch
 * when the project is already loaded elsewhere on the page.
 */
export function useProjectRole(
  projectId: string | null | undefined
): UseProjectRoleResult {
  const { data, isLoading } = useQuery({
    queryKey: ["project", projectId],
    queryFn: () =>
      projectId ? getProjectWithRfi(projectId) : Promise.reject("No project ID"),
    enabled: !!projectId,
  });

  const role = data?.current_user_role ?? null;
  const level = role ? ROLE_LEVELS[role] : 0;

  return {
    role,
    isLoading,
    canView: level >= ROLE_LEVELS.viewer,
    canEdit: level >= ROLE_LEVELS.editor,
    canManageMembers: level >= ROLE_LEVELS.manager,
  };
}
